import React from "react";
import { Box, Button } from "@mui/material";
import { noop } from "lodash";

import analytics from "fond/analytics";
import { LayerGroupIds } from "fond/layers";
import { PolygonState } from "fond/project/polygon/redux";
import { isMultiProject } from "fond/utils/project";
import { ConfirmModal } from "fond/widgets";

import Upload from "../upload/Upload";
import UploadProgress from "../upload/UploadProgress";

import DrawPolygon from "./DrawPolygon";
import { PolygonSelect } from "./usePolygonSelect";

interface PolygonSelectBodyProps {
  polygonSelect: PolygonSelect;
  onImport(): void;
  onCancel(): void;
}

/**
 * The polygon upload form, upload progress, or drawn polygon summary for the current polygon select state.
 */
const PolygonSelectBody: React.FC<PolygonSelectBodyProps> = ({ polygonSelect, onImport, onCancel }) => {
  const {
    polygon: { polygonState, selectedData, selectedArea, selectedLayers, incompleteData, status, isTooBig, downloadState },
    activeLayerGroupId,
    project,
    version,
    upload,
    isIncludeUgPathDisabled,
    convertToDualSided,
    setConvertToDualSided,
    selectedFiles,
    setSelectedFiles,
    isConfirmingReset,
    setIsConfirmingReset,
    onReset,
    onRetryDownload,
    toggleLayer,
    uploadPolygon,
    dismissPolygonUploadError,
  } = polygonSelect;

  if (!project || isMultiProject(project)) return null;

  if (upload) {
    return <UploadProgress upload={upload} onDismissUploadError={dismissPolygonUploadError} inputLayer="polygon" />;
  }

  if (polygonState === PolygonState.uploadingPolygon) {
    return (
      <Box px={1}>
        <Upload
          layer={{
            id: "polygon",
            geometryType: "Polygon",
            name: "Polygon",
          }}
          onSelectFiles={(validFiles) => {
            setSelectedFiles(validFiles);
          }}
          autoUpload={false}
          onBeginUpload={noop}
          onRemove={noop}
          includeLayerName={false}
          convertToDualSidedField={null}
        />
        <Button fullWidth onClick={uploadPolygon} variant="contained" disabled={selectedFiles === null || selectedFiles.length === 0}>
          Upload
        </Button>
      </Box>
    );
  }

  return (
    <>
      {isConfirmingReset && (
        <ConfirmModal
          open
          header="Resetting input area"
          content="Are you sure you wish to reset your input area? You will lose the area you have drawn."
          confirmText="Reset"
          onConfirm={() => {
            analytics.track("Reset area selection");
            onReset();
            setIsConfirmingReset(false);
          }}
          onCancel={() => setIsConfirmingReset(false)}
        />
      )}
      <Box px={1}>
        <DrawPolygon
          polygonState={polygonState}
          downloadState={downloadState}
          selectedLayers={selectedLayers}
          selectedData={selectedData}
          incompleteData={incompleteData}
          version={version}
          togglePolygonLayer={toggleLayer}
          convertToDualSided={convertToDualSided}
          setConvertToDualSided={setConvertToDualSided}
          setIsConfirmingReset={setIsConfirmingReset}
          onImportButtonClick={onImport}
          handleBack={onCancel}
          project={project}
          isTooBig={isTooBig}
          selectedArea={selectedArea}
          status={status}
          onReset={onReset}
          onRetryDownloadClick={onRetryDownload}
          isIncludeUgPathDisabled={isIncludeUgPathDisabled}
          isAddressGroup={activeLayerGroupId === LayerGroupIds.inAddress}
        />
      </Box>
    </>
  );
};

export default PolygonSelectBody;
